import { useState, useMemo, useCallback } from 'react';
import axiosInstance from '../../../api/axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Grid, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff, VpnKey, CheckCircle, Cancel } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTheme } from '@mui/material/styles';
import { notifyError, notifySuccess } from "../../../components/common/toast/ToastFunc";
import Image from "../../../assests/images/new_password.jpg";

const ResetPassword = () => {

  const theme = useTheme();

  const { token } = useParams();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ new_password: '', confirm_new_password: '', show_new_password: false, show_confirm_new_password: false });
  const [errors, setErrors] = useState({});

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const buttonTheme = useMemo(() => ({
    color: 'white',
    backgroundColor: theme.mainColor,
    fontSize: "18px",
    fontWeight: 'bold',
    borderRadius: "7px",
    height: "45px",
    '&:hover': {
      backgroundColor: '#b30000',
      color: 'white',
      fontSize: "18px",
      fontWeight: 'bold',
      borderRadius: "7px",
    },
  }), [theme.mainColor]);

  const textFieldTheme = useMemo(() => ({
    '& label.Mui-focused': {
      color: theme.mainColor,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'grey',
        borderRadius: '7px',
      },
      '&:hover fieldset': {
        borderColor: theme.mainColor,
      },
      '&.Mui-focused fieldset': {
        border: '1px solid #990000',
      },
    },
  }), [theme.mainColor]);

  const checkPasswordCriteria = useCallback((password) => {
    const lengthCheck = password.length >= 10;
    const uppercaseCheck = /[A-Z]/.test(password);
    const lowercaseCheck = /[a-z]/.test(password);
    const numberCheck = /\d/.test(password);
    const symbolCheck = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return lengthCheck && uppercaseCheck && lowercaseCheck && numberCheck && symbolCheck;
  }, []);

  const validateField = useCallback((name, value) => {
    switch (name) {
      case 'new_password':
        if (value.trim().length === 0) {
          return 'This field is required';
        }
        return checkPasswordCriteria(value.trim()) ? '' : 'Password criteria does not match';
      case 'confirm_new_password':
        if (value.length > 0) {
          return value === formData.new_password ? '' : 'Password does not match';
        }
        if (value.trim().length === 0) {
          return 'This field is required';
        }
        return '';
      default:
        return '';
    }
  }, [checkPasswordCriteria, formData.new_password]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };


  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      let newErrors = {};
      Object.keys(formData).forEach((key) => {
        const error = validateField(key, formData[key]);
        if (error) {
          newErrors[key] = error;
        }
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      setLoading(true);

      const response = await axiosInstance.post('/customer/user-info/reset-password', { new_password: formData.new_password, confirm_new_password: formData.confirm_new_password, reset_token: token });
      notifySuccess(response?.data?.message || "Success");
      navigate("/login");
    }
    catch (error) {
      notifyError(error.message || "Something went wrong");
    }
    finally {
      setLoading(false);
    }
  }, [formData, navigate, token, validateField]);

  return (
    <>
      <Grid container spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 65px)' }}>
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: "100%",
              height: "100%",
              mt: "65px",
              m: 2
            }}>
            <Typography gutterBottom sx={{ fontSize: "35px", color: "#990000", fontWeight: "bold" }}>
              Set New Password
            </Typography>
            <Box
              component="img"
              sx={{
                height: 470,
                width: 470,
              }}
              alt="Set New Password Image"
              src={Image} // Update this path with the actual image path
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={7}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            sx={{
              maxWidth: 400,
              p: 2,
            }}
          >
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ color: theme.mainColor, fontWeight: "500" }}>
                Enter your New Password
              </Typography>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type={formData.show_new_password ? 'text' : 'password'}
                  label="New Password"
                  variant="outlined"
                  name="new_password"
                  value={formData.new_password}
                  onChange={handleInputChange}
                  error={!!errors.new_password}
                  helperText={errors.new_password}
                  sx={{
                    ...textFieldTheme
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setFormData(prev => ({ ...prev, show_new_password: !prev.show_new_password }))}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {formData.show_new_password ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type={formData.show_confirm_new_password ? 'text' : 'password'}
                  label="Confirm New Password"
                  variant="outlined"
                  name="confirm_new_password"
                  value={formData.confirm_new_password}
                  onChange={handleInputChange}
                  sx={{
                    ...textFieldTheme
                  }}
                  error={!!errors.confirm_new_password}
                  helperText={errors.confirm_new_password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setFormData(prev => ({ ...prev, show_confirm_new_password: !prev.show_confirm_new_password }))}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {formData.show_confirm_new_password ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                {formData.new_password && (
                  <Typography
                    variant="body2"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      // mt: -2,
                      // mb: 2,
                      fontSize: "16px",
                      color: checkPasswordCriteria(formData.new_password) ? "green" : "red",
                    }}
                  >
                    {checkPasswordCriteria(formData.new_password) ?
                      <CheckCircle sx={{ fontSize: 20, mr: 1, color: "green" }} /> :
                      <Cancel sx={{ fontSize: 20, mr: 1, color: "red" }} />}
                    At least 10 characters long, a combination of uppercase letters, lowercase letters, numbers, and symbols.
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <LoadingButton
                  onClick={handleSubmit}
                  loading={loading}
                  loadingPosition="start"
                  startIcon={<VpnKey />}
                  variant="contained"
                  sx={{ ...buttonTheme }}
                  fullWidth
                >
                  New Password
                </LoadingButton>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default ResetPassword;
